import * as React from "react";
import events from "add-event-listener";
import debounce from "lodash.debounce";

import { MAX_WIDTH } from "../../constants";
import { emptyRef, isChildOfType, ofReactType } from "../../TypingUtils";
import { PanelType } from "../Panel/PanelTypes";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { LayoutEvents } from "../../Internals/LayoutEvents/LayoutEvents";
import { Icon } from "../../Icons/Icon";

import {
    ButtonMenu,
    CentersBlock,
    FormLayoutRoot,
    MenuIconContainer,
    PanelInnerContainer,
    PanelOuterContainer,
    TextContainer,
} from "./HiddenPanel.styles";
import { useDimensions, useWindowResize } from "./ReactEffects";
import { hiddenPanelTestPageObject } from "./HiddenPanelTestPageObject";

export interface HiddenPanelProps {
    isWidePanel: boolean;
    panelState: PanelType;
    onPanelStateChange?: (panelState: PanelType) => void;
    panelDisabled: boolean;
    children: React.ReactNode;
}

interface HiddenPanelMenuProps {
    className?: undefined | string;
    children: undefined | React.ReactNode;
}
export const HiddenPanelMenu = React.memo((props: HiddenPanelMenuProps) => (
    <>
        {React.Children.map(props.children, child =>
            React.isValidElement(child)
                ? React.cloneElement<HiddenPanelMenuProps>(child as React.ReactElement<HiddenPanelMenuProps>, {
                      className: props.className,
                  })
                : child
        )}
    </>
));
HiddenPanelMenu.displayName = "HiddenPanelMenu";

export const HiddenPanelContent = React.memo((props: { children: React.ReactNode }) => <>{props.children}</>);
HiddenPanelContent.displayName = "HiddenPanelContent";

export function HiddenPanel(props: HiddenPanelProps): JSX.Element {
    const panelContainer = React.useRef<HTMLDivElement>(emptyRef);

    React.useEffect(() => {
        if (panelContainer.current != undefined) {
            const currentPanelContainer = panelContainer.current;
            const callback = debounce(() => {
                LayoutEvents.emit();
            }, 50);
            events.addEventListener(currentPanelContainer, "transitionend", callback);
            events.addEventListener(currentPanelContainer, "transitionrun", callback);
            events.addEventListener(currentPanelContainer, "transitionstart", callback);
            return () => {
                events.removeEventListener(currentPanelContainer, "transitionend", callback);
                events.removeEventListener(currentPanelContainer, "transitionrun", callback);
                events.removeEventListener(currentPanelContainer, "transitionstart", callback);
            };
        }
        return undefined;
    }, [props.panelState]);

    useWindowResize(() => {
        if (props.panelState === "pinManual" || props.panelState === "hideManual") {
            return;
        }
        const isBigScreen = document.body && document.body.offsetWidth > MAX_WIDTH;
        if (isBigScreen) {
            props.onPanelStateChange?.("pin");
        } else {
            props.onPanelStateChange?.("hide");
        }
    }, [props.panelState]);

    const handleMouseOver = () => props.onPanelStateChange?.("show");

    const [containerRef, containerRect] = useDimensions({ liveMeasure: true });

    const children = React.Children.toArray(props.children);
    const panel = children.find(ofReactType(HiddenPanelMenu));
    const navigation = children.find(ofReactType(HiddenPanelContent));
    const restChildren = children.filter(
        x => !isChildOfType(x, HiddenPanelMenu) && !isChildOfType(x, HiddenPanelContent)
    );

    const { panelState, isWidePanel } = props;
    const showPanel = panelState === "show" || panelState === "pin" || panelState === "pinManual";
    const withPanel = panelState === "pin" || panelState === "pinManual";

    useTestPageObject(
        () => hiddenPanelTestPageObject(props.onPanelStateChange, panelState, handleMouseOver),
        [props.onPanelStateChange, props.panelState]
    );

    return (
        <FormLayoutRoot
            id="LoadedFormComponent"
            ref={containerRef}
            hasPanel={!props.panelDisabled && withPanel}
            widePanel={isWidePanel}>
            {!props.panelDisabled && (
                <>
                    {restChildren}
                    <PanelOuterContainer
                        ref={panelContainer}
                        visible={showPanel}
                        wide={isWidePanel}
                        fixed={containerRect != undefined && containerRect.top <= 0}>
                        <PanelInnerContainer>{panel}</PanelInnerContainer>
                    </PanelOuterContainer>
                </>
            )}
            {navigation}
            {!props.panelDisabled && (
                <ButtonMenu fixed={containerRect != undefined && containerRect.top <= 0} onMouseOver={handleMouseOver}>
                    <CentersBlock />
                    <MenuIconContainer>
                        <Icon name="UiMenuBars3HIcon" size={20} />
                    </MenuIconContainer>
                    <TextContainer>Меню</TextContainer>
                </ButtonMenu>
            )}
        </FormLayoutRoot>
    );
}
